import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Globe, Plane, Building2, TrendingUp, MapPin, Users, Shield } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article5 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-purple-600 text-white mr-4">
              <Globe className="h-3 w-3 mr-1" />
              Trade
            </Badge>
            <span className="text-sm text-gray-600">Ti Insight</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 2026
            <span className="mx-2">•</span>
            <span>6 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.5.title')}
          </h1>
          
          <img 
            src="https://cdn-trans.info/uploads/2020/11/85dab6c32f770a62baa189d6b73.jpg" 
            alt="EU Red Sea Mission"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.5.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.5.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.5.p1')}
            </p>

            <p className="mb-6">
              {t('article.5.p2')}
            </p>

            <div className="bg-purple-50 p-6 rounded-lg my-8 border border-purple-100">
              <h3 className="font-bold text-purple-900 mb-2 flex items-center">
                <Users className="mr-2" />
                {t('article.5.highlight.title')}
              </h3>
              <p className="text-sm text-purple-800">
                {t('article.5.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.5.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.5.p3')}
            </p>

            <p className="mb-6">
              {t('article.5.p4')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-blue-900 mb-4">{language === 'ko' ? 'ASPIDES 임무 세부사항' : 'ASPIDES Mission Details'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Shield className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '임무 범위' : 'Mission Scope'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '지역:' : 'Area:'}</strong> {language === 'ko' ? '홍해 및 아덴만' : 'Red Sea and Gulf of Aden'}</li>
                    <li><strong>{language === 'ko' ? '목적:' : 'Purpose:'}</strong> {language === 'ko' ? '상업 선박 보호' : 'Protect commercial vessels'}</li>
                    <li><strong>{language === 'ko' ? '위협:' : 'Threats:'}</strong> {language === 'ko' ? '후시 공격 방어' : 'Defend against Houthi attacks'}</li>
                    <li><strong>{language === 'ko' ? '참여국:' : 'Participants:'}</strong> {language === 'ko' ? 'EU 회원국 및 파트너' : 'EU members and partners'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Calendar className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '연장 기간' : 'Extension Period'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '시작:' : 'Started:'}</strong> {language === 'ko' ? '2024년 2월' : 'February 2024'}</li>
                    <li><strong>{language === 'ko' ? '첫 연장:' : 'First Extension:'}</strong> {language === 'ko' ? '2025년 2월까지' : 'Until February 2025'}</li>
                    <li><strong>{language === 'ko' ? '두 번째 연장:' : 'Second Extension:'}</strong> {language === 'ko' ? '2027년 2월까지' : 'Until February 2027'}</li>
                    <li><strong>{language === 'ko' ? '총 기간:' : 'Total Duration:'}</strong> {language === 'ko' ? '3년간 임무' : '3-year mission'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.5.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.5.p5')}
            </p>

            <p className="mb-6">
              {t('article.5.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.5.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.5.p7')}
            </p>

            <p className="mb-6">
              {t('article.5.p8')}
            </p>

            <div className="bg-red-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-red-900 mb-4">{language === 'ko' ? 'ASPIDES 임무 세부사항' : 'ASPIDES Mission Details'}</h3>
              <div className="space-y-4 text-sm text-red-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Shield className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '임무 범위' : 'Mission Scope'}
                  </h4>
                  <p>{language === 'ko' ? 
                    '유럽 위원회가 홍해, 아덴만, 아라비아해에서 해상 보호 임무를 계속하기 위해 €1,500만을 할당하여 2025년 6월까지 공격 후 최저점에서 60% 회복된 해상 교통을 보호합니다.' : 
                    'European Council allocates €15 million to continue naval protection mission in Red Sea, Gulf of Aden, and Arabian Sea, protecting marine traffic that has recovered 60% from post-attack lows by June 2025.'
                  }</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Calendar className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '연장 기간' : 'Extension Period'}
                  </h4>
                  <p>{language === 'ko' ? 
                    '임무는 2027년까지 연장되어 지역 내 상업 해상 교통의 안전과 보안을 지속적으로 보장하며, 국제 무역로의 안정성을 유지합니다.' : 
                    'The mission extends through 2027, ensuring continued safety and security of commercial maritime traffic in the region and maintaining stability of international trade routes.'
                  }</p>
                </div>
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Ti Insight - Europe and Asia entering a new era of trade integration.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://trans.info/en/eu-red-sea-mission-457737" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article5;